import React from 'react';
import { getMonthGrid, utcDateKey, getBrowserTimezone } from '@/utils/datetime';

const WEEKDAYS = ['Sun', 'Mon', 'Tue', 'Wed', 'Thu', 'Fri', 'Sat'];

export type DayBookingBlob = {
  id: string;
  title: string;
  status: string;
};

type Props = {
  year: number;
  month: number;
  selected: Date;
  bookingsByDayKey: Map<string, DayBookingBlob[]>;
  onSelectDay: (d: Date) => void;
  onPrevMonth: () => void;
  onNextMonth: () => void;
  timeZone?: string;
};

const MonthCalendar: React.FC<Props> = ({
  year,
  month,
  selected,
  bookingsByDayKey,
  onSelectDay,
  onPrevMonth,
  onNextMonth,
  timeZone,
}) => {
  const tz = timeZone || getBrowserTimezone();
  const weeks = getMonthGrid(year, month);
  const monthLabel = new Date(year, month, 1).toLocaleDateString('en-US', {
    month: 'long',
    year: 'numeric',
  });
  const todayKey = utcDateKey(new Date().toISOString(), tz);

  return (
    <div className="month-cal">
      <div className="month-cal-head">
        <button type="button" className="pill-btn" onClick={onPrevMonth} aria-label="Previous month">
          ‹
        </button>
        <h3>{monthLabel}</h3>
        <button type="button" className="pill-btn" onClick={onNextMonth} aria-label="Next month">
          ›
        </button>
      </div>
      <div className="month-cal-weekdays">
        {WEEKDAYS.map((w) => (
          <span key={w}>{w}</span>
        ))}
      </div>
      <div className="month-cal-grid">
        {weeks.flat().map((day, idx) => {
          if (!day) {
            return <div key={`empty-${idx}`} className="month-cal-cell empty" />;
          }
          const key = utcDateKey(day.toISOString(), tz);
          const blobs = bookingsByDayKey.get(key) || [];
          const isSelected =
            day.getDate() === selected.getDate() &&
            day.getMonth() === selected.getMonth() &&
            day.getFullYear() === selected.getFullYear();
          const isToday = key === todayKey;
          return (
            <button
              key={key}
              type="button"
              className={`month-cal-cell ${isToday ? 'today' : ''} ${isSelected ? 'selected' : ''} ${blobs.length ? 'has-events' : ''}`}
              onClick={() => onSelectDay(day)}
            >
              <span className="month-cal-day-num">{day.getDate()}</span>
              <div className="month-cal-blobs">
                {blobs.slice(0, 3).map((b) => (
                  <span
                    key={b.id}
                    className={`month-cal-blob status-${b.status}`}
                    title={b.title}
                  />
                ))}
                {blobs.length > 3 && <span className="month-cal-more">+{blobs.length - 3}</span>}
              </div>
            </button>
          );
        })}
      </div>
      <p className="month-cal-tz-hint">Times shown in {tz}</p>
    </div>
  );
};

export default MonthCalendar;
